import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTheme } from "../../../providers/ThemeContext";
import { useI18n } from "../../../languages/providers/I18nProvider";
import { useMediaQuery } from "@mui/material";

import AutoSlider from "../../../components/autoSlider/AutoSlider";
import { ServiceDetailsStyles } from "./ServiceDetailsStyles";
import { ServiceDetails } from "./ServiceDetailsData";
import CallToAction from "../../../components/common/CallToAction";
import Card from "../../../components/cards/card/Card";
import { GlobalStyles } from "../../../styles/GlobalStyles";
import FaqAccordion from "../../../components/common/FaqAccordion";
import {
  CategoryCardData,
  CategoryNode,
} from "../../../types/RandomGlobalTypes";
import SEO from "../../../components/common/SEO";
import { seoConfig, buildSchema } from "../../../config/seoConfig";
import { useLangPath } from "../../../hooks/useLangPath";
import NotFoundPage from "../../notFound/Notfoundpage";

const BASE = "https://creationarts.com";

const ServiceDetailsPage = () => {
  const { slug } = useParams();
  const { theme } = useTheme();
  const { t, lang } = useI18n();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const lp = useLangPath();

  const s = ServiceDetailsStyles(theme, isMobile);
  const g = GlobalStyles(theme);

  const svc = useMemo(
    () => ServiceDetails.find((x) => x.slug === slug),
    [slug],
  );
  if (!svc) return <NotFoundPage />;
  const tagKeys = (svc.tagsKeys ?? []).filter(Boolean);
  const categoryCards =
    svc.categoryCards?.map((c) => ({
      title: t(c.titleKey),
      desc: c.descKey ? t(c.descKey) : undefined,
      img: c.img,
      alt: c.alt,
      to: c.to,
    })) ?? [];

  const categories = (svc.categoriesKeys ?? []).filter(Boolean);
  const categoryGroups = (svc.categoryGroups ?? []).filter(
    (g) => g && Array.isArray(g.itemKeys) && g.itemKeys.length > 0,
  );

  const specEntries = svc.specs
    ? Object.entries(svc.specs)
        .filter(([labelKey, valueKey]) => labelKey && valueKey)
        .map(([labelKey, valueKey]) => ({
          label: t(labelKey),
          value: t(valueKey),
        }))
    : [];

  // Returns translated text only if the key resolves to an actual translation
  const tIfExists = (key?: string): string | null => {
    if (!key) return null;
    const text = t(key);
    return text !== key ? text : null;
  };

  // ---------- helpers ----------
  const renderCards = (cards: CategoryCardData[]) => (
    <div style={g.cardGrid}>
      {cards.map((c) => (
        <Card
          key={c.titleKey}
          title={t(c.titleKey)}
          titleColor={theme.primary2}
          desc={c.descKey ? t(c.descKey) : undefined}
          img={c.img}
          alt={c.alt}
          interactive={false}
          imgRadius={0}
          radius={0}
          titleWeight={600}
          titleSize={17}
          headingTag="h3"
        />
      ))}
    </div>
  );

  const renderNode = (node: CategoryNode, level = 0) => {
    const bullets = (node.pointsKeys ?? []).filter(Boolean);
    const hasCards = (node.cards ?? []).length > 0;
    const subtypeEntries = node.subtypes ? Object.entries(node.subtypes) : [];
    const introText = tIfExists(node.introKey);

    return (
      <section key={node.titleKey + level} style={s.section}>
        {level === 0 ? (
          <h2 style={s.typePill}>{t(node.titleKey)}</h2>
        ) : (
          <h3 style={s.subtypePill}>{t(node.titleKey)}</h3>
        )}

        {introText && <p style={s.sectionPara}>{introText}</p>}

        {(node.descKey || bullets.length > 0) && (
          <div style={s.sectionDesc}>
            {node.descKey ? (
              <p style={s.sectionPara}>{t(node.descKey)}</p>
            ) : null}
            {bullets.length > 0 ? (
              <ul style={s.pointsList}>
                {bullets.map((k) => (
                  <li key={k}>{t(k)}</li>
                ))}
              </ul>
            ) : null}
          </div>
        )}

        {hasCards ? renderCards(node.cards!) : null}

        {subtypeEntries.length > 0
          ? subtypeEntries.map(([k, sub]) => (
              <div key={k} style={s.subtypeBlock}>
                {renderNode(sub, level + 1)}
              </div>
            ))
          : null}
      </section>
    );
  };

  // ---------- JSX ----------
  return (
    <main style={s.page}>
      {/* ===== SEO ===== */}
      {seoConfig[slug!] && (
        <SEO
          title={t(seoConfig[slug!].titleKey)}
          description={t(seoConfig[slug!].descriptionKey)}
          canonical={`${BASE}/${lang}${seoConfig[slug!].path}`}
          lang={lang}
          schema={buildSchema(
            t(seoConfig[slug!].titleKey),
            t(seoConfig[slug!].descriptionKey),
            `${BASE}/${lang}${seoConfig[slug!].path}`,
            lang,
            svc.faq?.map(({ qKey, aKey }) => ({ q: t(qKey), a: t(aKey) })),
          )}
        />
      )}
      {/* ===== Breadcrumb ===== */}
      <nav aria-label="breadcrumb" style={s.breadcrumbNav}>
        <ol style={s.breadcrumbOl}>
          <li style={s.breadcrumbItem}>
            <Link to={lp("")} style={s.breadcrumbLink}>
              {t("nav:home")}
            </Link>
            <span style={s.breadcrumbSep} aria-hidden="true">
              ›
            </span>
          </li>
          <li style={s.breadcrumbItem}>
            <Link to={lp("/services")} style={s.breadcrumbLink}>
              {t("nav:services")}
            </Link>
            <span style={s.breadcrumbSep} aria-hidden="true">
              ›
            </span>
          </li>
          <li>
            <span style={s.breadcrumbCurrent} aria-current="page">
              {t(svc.titleKey)}
            </span>
          </li>
        </ol>
      </nav>

      {/* ===== Hero ===== */}
      <section style={s.hero}>
        <div style={s.heroInner}>
          <div id="top">
            {svc.heroBadgeKey && (
              <span style={s.badge}>{t(svc.heroBadgeKey)}</span>
            )}
            <h1 style={s.title}>{t(svc.titleKey)}</h1>
            <p style={s.subtitle}>{t(svc.subtitleKey)}</p>

            {tagKeys.length > 0 && (
              <div
                style={s.tagsRow}
                aria-label={t("services:common:highlights")}
              >
                {tagKeys.map((k) => (
                  <span key={k} style={s.tagPill}>
                    {t(k)}
                  </span>
                ))}
              </div>
            )}

            {svc.descriptionKey && (
              <div style={s.des}>
                <h2 style={s.sectionTitle}>
                  {t("services:common:description")}
                </h2>
                <p
                  style={{
                    fontSize: 15,
                    lineHeight: 1.7,
                    opacity: 0.95,
                    marginTop: 6,
                  }}
                >
                  {t(svc.descriptionKey)}
                </p>
              </div>
            )}
          </div>

          <AutoSlider
            slides={svc.media}
            width={720}
            height={398}
            isRTL={lang === "ar"}
          />
        </div>
      </section>

      {/* ===== Categories logic (A/B/C) ===== */}
      {svc.typesTree && Object.keys(svc.typesTree).length > 0 ? (
        <>
          <section style={s.section}>
            <h2 style={s.sectionTitle}>{t("services:common:categories")}</h2>
          </section>
          {Object.entries(svc.typesTree).map(([k, node]) => (
            <div key={k}>{renderNode(node, 0)}</div>
          ))}
        </>
      ) : categoryCards.length > 0 ? (
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{t("services:common:categories")}</h2>
          <div style={g.cardGrid}>
            {categoryCards.map((c) => (
              <Card
                key={c.title}
                title={c.title}
                titleColor={theme.primary2}
                desc={c.desc}
                img={c.img}
                alt={c.alt}
                interactive={false}
                headingTag="h3"
              />
            ))}
          </div>
        </section>
      ) : categories.length > 0 ? (
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{t("services:common:categories")}</h2>
          <div style={s.categoryGrid}>
            {categories.map((k) => (
              <span key={k} style={s.categoryTag}>
                {t(k)}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      {/* CATEGORY GROUPS (title + bullets) */}
      {categoryGroups.length > 0 ? (
        <section style={s.section}>
          {categoryGroups.map((grp) => (
            <div key={grp.titleKey} style={s.categoryGroup}>
              <div style={s.categoryTitle}>{t(grp.titleKey)}</div>
              <ul style={s.categoryList}>
                {(grp.itemKeys ?? []).filter(Boolean).map((ik) => (
                  <li key={ik}>{t(ik)}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ) : null}

      {/* ===== FEATURES ===== */}
      {svc.featuresKeys?.length ? (
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{t("services:common:features")}</h2>
          <ul style={s.pointsList}>
            {svc.featuresKeys.map((k) => (
              <li key={k}>{t(k)}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ===== SPECS ===== */}
      {specEntries.length > 0 ? (
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{t("services:common:specs")}</h2>
          {tIfExists(svc.specsIntroKey) && (
            <p style={{ ...s.sectionPara, marginBottom: 10 }}>
              {tIfExists(svc.specsIntroKey)}
            </p>
          )}
          <div style={s.specs}>
            {specEntries.map((row, i) => (
              <div
                key={row.label}
                style={{ ...s.specRow, ...(i === 0 ? s.specFirst : {}) }}
              >
                <div style={s.specLabel}>{row.label}</div>
                <div>{row.value}</div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* ===== PROCESS ===== */}
      {svc.processKeys?.length ? (
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{t("services:common:process")}</h2>
          <ol style={s.processList}>
            {svc.processKeys.map((k, idx) => (
              <li key={k} style={s.processItem}>
                <div style={s.stepBadge}>{idx + 1}</div>
                <div style={s.stepLabel}>{t(k)}</div>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {/* ===== FAQ ===== */}
      {svc.faq?.length ? (
        <section style={s.section} id="faq">
          <h2 style={s.sectionTitle}>{t("services:common:faq")}</h2>
          {tIfExists(svc.faqIntroKey) && (
            <p style={{ ...s.sectionPara, marginBottom: 12 }}>
              {tIfExists(svc.faqIntroKey)}
            </p>
          )}
          <FaqAccordion
            items={svc.faq.map(({ qKey, aKey }) => ({
              q: t(qKey),
              a: t(aKey),
            }))}
            defaultOpen={0}
          />
        </section>
      ) : null}

      {/* ===== RELATED SERVICES ===== */}
      {svc.relatedServices && (
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{t(svc.relatedServices.titleKey)}</h2>
          <div style={s.relatedGrid}>
            {svc.relatedServices.items.map((item) => (
              <Link key={item.href} to={lp(item.href)} style={s.relatedLink}>
                <span>{t(item.textKey)}</span>
                <span aria-hidden="true">→</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ===== CTA ===== */}
      <section style={s.finalCta}>
        <CallToAction
          title={t("services:common:readyToStart")}
          buttonLabel={t(svc.ctaLabelKey || "services:detail:cta:getQuote")}
        />
      </section>
    </main>
  );
};

export default ServiceDetailsPage;
